import { motion } from "framer-motion";
import { ArrowRight, Trophy } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";

const PIPELINE = [
  "Market Research",
  "Segmentation",
  "Positioning",
  "Messaging",
  "Packaging",
  "Sales Enablement",
  "Launch",
  "Market Growth",
];

const CRAFT = [
  "Product Strategy",
  "Competitive Analysis",
  "Product Differentiation",
  "Annual Operating Plan",
  "Budgeting",
  "Pricing",
];

export const ChapterSix = () => (
  <ChapterShell
    n="06"
    id="chapter-6"
    era="The Launch Engine"
    title="Launching Brands"
    quote="Every launch is a laboratory: research in, market growth out."
  >
    <FadeUp className="overflow-x-auto pb-4">
      <div className="flex min-w-max items-stretch gap-3">
        {PIPELINE.map((step, i) => (
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: i * 0.09 }}
            className="flex items-center gap-3"
          >
            <div
              className={`glass flex h-28 w-40 flex-col justify-between rounded-2xl p-4 ${
                step === "Launch" ? "animate-glow border-[#00E5FF]/60" : ""
              }`}
            >
              <span className="font-display text-[10px] font-black text-[#0A192F]/35">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display text-sm font-bold uppercase leading-tight tracking-[0.1em] text-[#0A192F]">
                {step}
              </span>
            </div>
            {i < PIPELINE.length - 1 && <ArrowRight size={16} className="shrink-0 text-[#00BFA5]" />}
          </motion.div>
        ))}
      </div>
    </FadeUp>

    <FadeUp delay={0.15} className="mt-10 flex flex-wrap gap-3">
      {CRAFT.map((c) => (
        <span
          key={c}
          className="rounded-full border border-[#0A192F]/15 bg-white/60 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-[#0A192F]/70"
        >
          {c}
        </span>
      ))}
    </FadeUp>

    <FadeUp delay={0.2} className="mt-16 rounded-[2rem] bg-[#0A192F] p-8 text-white md:p-12">
      <div className="flex flex-wrap items-center justify-between gap-8">
        <div className="max-w-xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00E5FF]">Marketing Innovation</p>
          <h3 className="mt-3 font-display text-3xl font-black tracking-tight md:text-4xl">
            "Cheer for India" Marketing Campaign
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-white/60 md:text-base">
            A campaign that moved beyond the brand — and into the record books.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          {["India Book of Records", "Asia Book of Records"].map((r, i) => (
            <motion.div
              key={r}
              initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.15, type: "spring" }}
              className="glass-dark flex items-center gap-3 rounded-2xl px-6 py-4"
              data-testid={`record-badge-${i}`}
            >
              <Trophy size={20} className="text-[#00E5FF]" />
              <span className="font-display text-sm font-bold uppercase tracking-[0.14em]">{r}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </FadeUp>
  </ChapterShell>
);
